import { useGetAssignment, useGetAssignmentVisuals } from 'dataQueries'
import { SignedURLDTO, UrlDTO } from 'models/visuals'
import { useMemo, useState } from 'react'
import { AssignmentDTOIsCreativeDTO, VisualListResponseIsCreativeResponse } from 'utils/typeguards'

import { VisualUploadEndpoints } from 'constants/API'
import { VisualFileType } from 'constants/visual'
import constate from 'constate'
import { StatusResponse } from 'models/redux'
import { chooseProductContainingVisuals } from 'utils/product'
import { useFileAPI } from '../../FileAPI'
import { UploadType } from '../../FileAPI/fileUtils'
import { CreativeUploadWizardStep } from './CreativeUploadWizard.controller'

export const [CreativeUploadWizardContext, useCreativeUploadWizard] = constate(({ assignmentId }: { assignmentId: string }) => {

  const visualsRequirementsQuery = useGetAssignmentVisuals(assignmentId, VisualFileType.RAW)
  const assignmentQuery = useGetAssignment(assignmentId)

  const fileCtrl = useFileAPI('CT_UPLOAD_WIZARD', {
    uploadUrlResolver: (entry, api) => api.post<SignedURLDTO>(
      VisualUploadEndpoints.GET_UPLOAD_URL,
      { assignmentId },
      {
        filename: entry.fileObject.name,
        contentType: entry.fileObject.type,
        type: VisualFileType.RAW,
      },
      true,
      {
        params: {
          uploadType: UploadType.RESUMABLE
        }
      }
    ),
    reUploadUrlResolver: (entry, api) => api.post<SignedURLDTO>(
      VisualUploadEndpoints.GET_UPLOAD_URL,
      { assignmentId },
      {
        filename: entry.fileObject.name,
        contentType: entry.fileObject.type,
        type: VisualFileType.RAW,
      },
      true,
      {
        params: {
          uploadType: UploadType.RESUMABLE
        }
      }
    ),
    loadUrlResolver: (entry, api) => api.post<UrlDTO>(
      VisualUploadEndpoints.GET_LOAD_URL,
      {
        assignmentId,
        filename: entry.gcFilename!,
      },
      {},
      true,
      {
        params: {
          type: VisualFileType.RAW_THUMB
        }
      }
    ),
    deleteHandler: (entry, api) => api.delete<StatusResponse>(
      VisualUploadEndpoints.DELETE,
      {
        assignmentId,
        filename: entry.gcFilename!,
      },
      true,
      {
        params: {
          type: VisualFileType.RAW
        }
      }
    )
  })

  const [isSubmitOpen, setIsSubmitOpen] = useState(false)

  const requiredVisualAmount = useMemo(() => {
    const visualData = visualsRequirementsQuery.data?.data
    if (!visualData || !VisualListResponseIsCreativeResponse(visualData)) return 0

    return visualData.minUploadCount
  }, [visualsRequirementsQuery.data?.data])

  const uploadedRequiredVisualsAmount = useMemo(() => fileCtrl.filesByTag[CreativeUploadWizardStep.REQUIRED_VISUALS]?.length ?? 0, [fileCtrl.filesByTag])

  const areVisualsMissing = useMemo(() => requiredVisualAmount > uploadedRequiredVisualsAmount, [requiredVisualAmount, uploadedRequiredVisualsAmount])
  const isOverRequiredLimit = useMemo(() => uploadedRequiredVisualsAmount > requiredVisualAmount, [requiredVisualAmount, uploadedRequiredVisualsAmount])

  const assignmentData = useMemo(() => {
    if (!assignmentQuery.data?.data || !AssignmentDTOIsCreativeDTO(assignmentQuery.data.data)) return

    return assignmentQuery.data.data
  }, [assignmentQuery?.data?.data])

  const assignmentProduct = useMemo(() => {
    if (!assignmentData?.products) return

    return chooseProductContainingVisuals(assignmentData?.products)
  }, [assignmentData?.products])

  return {
    fileCtrl,
    assignmentId,
    isSubmitOpen,
    requiredVisualAmount,
    areVisualsMissing,
    assignmentData,
    assignmentProduct,
    isOverRequiredLimit,
    uploadedRequiredVisualsAmount,
    visualsRequirementsQuery,
    assignmentQuery,
    setIsSubmitOpen,
  }
})
